import React, { useState, useContext, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import usePostData from "Hooks/usePostData";
import Spinner from "Components/RequestHandler/Spinner";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import { ShieldCheck } from "@phosphor-icons/react";
import AuthProgressBar from "Components/AuthProgressBar/AuthProgressBar";
import logo from "assests/logos/dclass.png";

const VerifyEmail = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, token, isVerified, setIsVerifiedHandler } = useContext(LoginAuthContext);
  const { postData, isLoading } = usePostData();
  const otpRef = useRef(null);

  const [otp, setOtp] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);

  const getToken = useCallback(() => {
    return token || localStorage.getItem("token");
  }, [token]);

  // If already verified, redirect to plans
  useEffect(() => {
    if (isVerified) {
      navigate("/plans");
    }
  }, [isVerified, navigate]);

  // If not logged in, redirect to register
  useEffect(() => {
    const timer = setTimeout(() => {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");
      const hasUserInContext = user || token;
      const hasUserInStorage = storedToken || storedUser;

      if (!hasUserInContext && !hasUserInStorage) {
        navigate("/register");
      }
    }, 100);
    return () => clearTimeout(timer);
  }, [user, token, navigate]);

  const getUserEmail = useCallback(() => {
    if (user?.email) return user.email;
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const parsed = JSON.parse(storedUser);
        return parsed?.email;
      } catch {
        return null;
      }
    }
    return null;
  }, [user]);

  // Auto-send OTP on page load
  useEffect(() => {
    const email = getUserEmail();
    if (email && !otpSent) {
      sendOtp(email);
    }
  }, [getUserEmail]);

  // Resend countdown timer
  useEffect(() => {
    if (resendCountdown <= 0) return;
    const interval = setInterval(() => {
      setResendCountdown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [resendCountdown]);

  const sendOtp = async (email) => {
    setErrorMessage("");
    setSuccessMessage("");

    if (!email) {
      setErrorMessage(t("verify_email.send_error"));
      return;
    }

    try {
      const response = await postData("auth/send-verification", { email });

      if (response.success) {
        setOtpSent(true);
        setSuccessMessage(t("verify_email.send_success"));
        setResendCountdown(60);
        // Auto-focus OTP input
        setTimeout(() => otpRef.current?.focus(), 300);
      } else {
        setErrorMessage(response.message || t("verify_email.send_error"));
      }
    } catch (error) {
      setErrorMessage(t("verify_email.send_error"));
    }
  };

  const handleResend = () => {
    const email = getUserEmail();
    if (email && resendCountdown === 0) {
      sendOtp(email);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp || otp.length < 4) {
      setErrorMessage(t("verify_email.otp_required"));
      return;
    }

    const email = getUserEmail();
    setErrorMessage("");
    setSuccessMessage("");
    setIsVerifying(true);

    try {
      const payload = { email, code: otp };
      const response = await postData("auth/verify-email", payload);

      if (response.success) {
        setIsVerifiedHandler(true);
        setSuccessMessage(t("verify_email.verified_success"));
        setTimeout(() => {
          navigate("/plans");
        }, 1000);
      } else {
        setErrorMessage(response.message || t("verify_email.verify_error"));
      }
    } catch (error) {
      setErrorMessage(t("verify_email.verify_error"));
    } finally {
      setIsVerifying(false);
    }
  };

  // Submit on Enter key
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && otp.length >= 4) {
      handleVerifyOtp();
    }
  };

  const userEmail = getUserEmail();

  return (
    <main className="min-h-screen bg-gradient-to-br from-bg via-grey to-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Link to="/">
            <img src={logo} alt="Dee Class" className="h-12" />
          </Link>
        </div>

        {/* Progress Bar */}
        <AuthProgressBar currentStep="verify" />

        {/* Verify Card */}
        <div className="bg-grey/80 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-gray-700/50">
          {/* Icon */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center">
              <ShieldCheck size={36} className="text-primary" />
            </div>
          </div>

          {/* Header */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-white mb-2">
              {t("verify_email.title")}
            </h1>
            {userEmail && (
              <p className="text-gray-400 text-sm">
                {t("verify_email.sent_to") || "We sent a code to"}{" "}
                <span className="text-white font-medium">{userEmail}</span>
              </p>
            )}
          </div>

          {/* Messages */}
          {successMessage && (
            <div className="bg-green-500/10 border border-green-500/50 text-green-400 px-4 py-3 rounded-xl mb-6 text-sm text-center">
              {successMessage}
            </div>
          )}
          {errorMessage && (
            <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-xl mb-6 text-sm text-center">
              {errorMessage}
            </div>
          )}

          {/* OTP Input */}
          <div className="mb-6">
            <input
              ref={otpRef}
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
              onKeyDown={handleKeyDown}
              placeholder="000000"
              className="w-full bg-bg/50 border border-gray-600 text-white py-4 px-4
                rounded-xl placeholder:text-gray-600
                focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary/50
                transition-all duration-300 text-center text-3xl tracking-[0.5em] font-mono"
              maxLength={6}
              autoComplete="one-time-code"
              inputMode="numeric"
            />
          </div>

          {/* Verify Button */}
          <button
            className="w-full bg-primary hover:bg-primary/90 text-white py-3.5 px-6 rounded-xl font-semibold
              transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed
              flex items-center justify-center gap-2"
            onClick={handleVerifyOtp}
            disabled={isVerifying || !otp || otp.length < 4}
          >
            {isVerifying ? (
              <Spinner isSmall={true} isWhite={true} />
            ) : (
              t("verify_email.verify_button")
            )}
          </button>

          {/* Resend */}
          <div className="text-center mt-5">
            {resendCountdown > 0 ? (
              <p className="text-gray-500 text-sm">
                {t("verify_email.resend_in") || "Resend code in"}{" "}
                <span className="text-white font-medium">{resendCountdown}s</span>
              </p>
            ) : (
              <button
                onClick={handleResend}
                disabled={isLoading}
                className="text-primary hover:text-primary/80 text-sm font-medium transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Spinner isSmall={true} />
                ) : (
                  t("verify_email.resend_button")
                )}
              </button>
            )}
          </div>

          {/* Help text */}
          <p className="text-xs text-gray-500 mt-6 text-center">
            {t("verify_email.help_text")}
          </p>
        </div>
      </div>
    </main>
  );
};

export default VerifyEmail;
